/**
 * 팀 쿼리 키 팩토리.
 *
 * 이 도메인은 키 정의가 **세 군데로 갈려** 있었다:
 * - `entities/team/model/useTeamQueries.ts` 의 `TEAM_KEYS` (일부만 팩토리, 이름도 이것만 대문자)
 * - `features/team-settings/model/useTeamBudgetAllocation.ts` 의 `TEAM_BUDGET_ALLOCATION_KEY`
 * - `useTeamSettings.ts` · `HrManagementPage` · `useManagementAdapter` 등의 인라인 리터럴
 *
 * 팀 mutation 은 전부 `'teams'` 루트를 무효화하므로 **루트 안에 있는 것이 곧 갱신 보장**이다.
 * 루트 밖으로 나가면 무효화가 한 건도 안 걸려 타입 에러 없이 조용히 stale 된다 —
 * 실제로 `['all-members']` 가 루트 밖에 있었다(아래 `allMembers` 주석).
 *
 * 회귀 가드: `__tests__/teamKeys.test.ts`.
 */
import { defineInvalidation, defineQueryKeys } from '@/shared/lib/queryKeys';

const keys = defineQueryKeys('teams', (all) => ({
  /**
   * ── 무효화 전용 중간 접두사 ────────────────────────────────────────────
   *
   * 조회에 쓰지 않는다. 뮤테이션이 **무엇을 무효화하는지 선언**할 때 쓴다
   * (아래 `defineInvalidation` 계약).
   */
  listAll: [...all, 'list'] as const,
  detailAll: [...all, 'detail'] as const,
  membersAll: [...all, 'members'] as const,

  /**
   * 팀 설정 화면의 팀 목록.
   *
   * `lists()` 와 **다른 캐시 엔트리**인데 둘 다 `GET /teams/list` 를 부르고 CLUB 을 걸러낸다
   * (`teamSettingsApi.fetchTeams` vs `teamApi.getTeams`, 반환 타입만 다름). 같은 응답을 두 번
   * 받아 두 자리에 저장하는 셈이다 — 중복 요청 문제이고 stale 문제는 아니다. 통합은 반환
   * 타입이 달라 별도 판단이 필요해 현재 모양을 그대로 둔다.
   *
   * **세그먼트를 붙였다.** 예전에는 `['teams']` — 루트 그 자체였다. 그러면 "목록만 무효화" 가
   * 곧 "도메인 전체" 가 되어 계약이 표현 불가능하다. `lists()` 와 같은 `'list'` 하위에 둬서
   * 한 scope 가 두 캐시를 함께 덮게 한다(같은 엔드포인트이므로 갈릴 이유가 없다).
   */
  settingsList: () => [...all, 'list', 'settings'] as const,

  /** 운영팀 목록(`entities/team` 쪽). */
  lists: () => [...all, 'list'] as const,
  /** 내가 속한 팀. */
  myTeams: () => [...all, 'my-teams'] as const,
  /** 팀 운영 로스터. */
  teamOpsRoster: () => [...all, 'team-ops-roster'] as const,
  /** 팀 상세. */
  detail: (id: string) => [...all, 'detail', id] as const,
  /**
   * 특정 팀의 구성원.
   *
   * **세그먼트 순서를 바꿨다.** 예전에는 `['teams', teamId, 'members']` 로 uuid 가 1번 칸에
   * 들어가 리터럴(`'list'` · `'detail'` · `'all-members'` …)과 같은 자리를 나눠 썼다. 그러면
   * "구성원 전부" 접두사를 만들 수 없고, teamId 가 우연히 그 리터럴이면 충돌한다.
   */
  members: (teamId: string | undefined) => [...all, 'members', teamId] as const,

  /**
   * 전사 구성원 — 팀 설정·시나리오·버그리포트 세 화면이 공유한다.
   *
   * `widgets/CarouselDock/lib/prefetchByView.ts` 가 루트 **밖**인 `['all-members']` 로 워밍하고
   * 있어 hover 로 받아온 응답을 아무도 읽지 못했다. 요청은 실제로 나가고, 팀 설정을 열면
   * 같은 데이터를 다시 받았다. 이 키로 통일해 그 요청이 제 자리에 들어가게 한다.
   */
  allMembers: () => [...all, 'all-members'] as const,

  /** 예산 분배 무효화용 prefix — teamId·period 조합 전체를 매칭한다. */
  budgetAllocationAll: [...all, 'budget-allocation'] as const,
  /**
   * 팀 예산 분배. `period` 유무로 **칸 수가 갈린다**(기존 동작 그대로) — 서버가 period 미지정 시
   * 현 KST period 를 쓰므로 두 호출이 같은 데이터를 가리킬 수 있지만, 캐시 엔트리는 별개다.
   */
  budgetAllocation: (teamId: string | undefined, period?: string) =>
    period
      ? ([...all, 'budget-allocation', teamId, period] as const)
      : ([...all, 'budget-allocation', teamId] as const),
}));

/**
 * 무효화 계약.
 *
 * - `list` — 팀 목록. `settingsList()` 와 `lists()` **둘 다** 덮는다(같은 엔드포인트의 두 캐시)
 * - `myTeams` — 내가 속한 팀. `capabilities` 를 실어 나른다
 * - `roster` — 팀 운영 로스터(`useTeamOpsRoster`)
 * - `detail` — 팀 상세
 * - `members` — 특정 팀의 구성원
 * - `allMembers` — 전사 구성원. 팀 설정·시나리오·버그리포트 세 화면이 공유한다
 * - `budget` — 팀 예산 분배
 *
 * `teamRoot` 는 **배럴로 내보내지 않는다** — 열면 계약을 우회할 수 있다.
 */
export const {
  keys: teamKeys,
  root: teamRoot,
  invalidate: invalidateTeamScopes,
} = defineInvalidation(keys, {
  list: keys.listAll,
  myTeams: keys.myTeams(),
  roster: keys.teamOpsRoster(),
  detail: keys.detailAll,
  members: keys.membersAll,
  allMembers: keys.allMembers(),
  budget: keys.budgetAllocationAll,
});
